import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableRow,
  TableCell,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { AttachMoney, ExpandMore } from "@mui/icons-material";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  createTrainingCost,
  deleteTrainingCost,
  getTrainingCostsByParticipant,
  updateTrainingCost,
} from "../../../reducers/TrainingCostReducer";
import PATAddButton from "../../../components/Buttons/PATAddButton";
import CustomTable from "../../../components/CustomTable";
import PATEditButton from "../../../components/Buttons/PATEditButton";
import PATDeleteButton from "../../../components/Buttons/PATDeleteButton";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";
import {
  decimalStringTest,
  formatToTwoDecimals,
  getCodeId,
  mapDictionaryOptions,
  maxLengthMessage,
} from "../../../utils/functions";
import DropdownController from "../../../components/inputs/DropdownController";
import TextFieldController from "../../../components/inputs/TextFieldController";

const schema = yup.object().shape({
  title: yup
    .string()
    .max(50, maxLengthMessage(50))
    .required("Code is required")
    .nonNullable(),
  cost: decimalStringTest("Value", 15, 2).required("Cost is required"),
});

const tableHeaders = ["Code", "Cost", "Actions"];

const ParticipantCostTable = ({
  id,
  theme,
  participantId,
  participantCosts,
  allExpanded = true,
  canAdd,
  canEdit,
  canDelete,
}) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);
  const user = useSelector((state) => state.user);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [costToDelete, setCostToDelete] = useState(null);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [editingData, setEditingData] = useState(null);

  const [expanded, setExpanded] = useState(allExpanded);
  const trainingCostOptions = mapDictionaryOptions(
    dictionary.codePerTrainingCost
  );

  useEffect(() => {
    setExpanded(allExpanded);
  }, [allExpanded]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: "",
      cost: "",
    },
  });

  useEffect(() => {
    if (editingData) {
      reset({
        title: editingData.title,
        cost: formatToTwoDecimals(editingData.cost),
      });
    } else {
      reset();
    }
  }, [editingData, reset]);

  const handleOpenDeleteDialog = (e, costId) => {
    e.stopPropagation();
    setCostToDelete(costId);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (costToDelete) {
      dispatch(
        deleteTrainingCost(costToDelete, userData?.user?.ptsUserID)
      ).then(() => dispatch(getTrainingCostsByParticipant(participantId)));
    }
    setOpenDeleteDialog(false);
    setCostToDelete(null);
  };

  const handleOpenFormDialog = (e, trainingCost = null) => {
    e.stopPropagation();
    setEditingData(trainingCost);
    setOpenFormDialog(true);
  };

  const handleCloseFormDialog = () => {
    setOpenFormDialog(false);
    setEditingData(null);
    reset({
      title: "",
      cost: "",
    });
  };

  const onSubmit = (data) => {
    const payload = {
      ...data,
      perTrainingId: participantId,
      titleId: data?.title ? getCodeId(data.title, dictionary.codePerTrainingCost) : null,
      agencyId: user?.userAgency || null,
    };

    const action = editingData
      ? updateTrainingCost(editingData.id, { ...payload })
      : createTrainingCost(payload);

    dispatch(action).then(() => {
      dispatch(getTrainingCostsByParticipant(participantId));
      handleCloseFormDialog();
    });
  };

  const totalCost = useMemo(() => {
    if (!participantCosts || participantCosts.length === 0) return 0;
    return participantCosts.reduce(
      (sum, particiapnt) => sum + Number(particiapnt.cost || 0),
      0
    );
  }, [participantCosts]);

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: theme.accordion.backgroundColor,
            color: theme.accordion.textColor,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" alignItems="center">
              <AttachMoney sx={{ mr: 1 }} />
              <span>Training Costs</span>
            </Box>
            <Box display="flex" alignItems="center" sx={{ mr: 2 }}>
              <span style={{ marginRight: 16 }}>
                Total: ${formatToTwoDecimals(totalCost)}
              </span>
              {canAdd && (
                <PATAddButton
                  tooltipText="Add Training Cost"
                  onClick={(e) => handleOpenFormDialog(e)}
                />
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {participantCosts && participantCosts.length > 0 ? (
            <CustomTable
              columns={tableHeaders}
              data={participantCosts}
              renderRow={(trainingCost, index) => (
                <TableRow key={index}>
                  <TableCell>{trainingCost.title}</TableCell>
                  <TableCell>{`$${formatToTwoDecimals(
                    trainingCost.cost
                  )}`}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {canEdit && (
                        <PATEditButton
                          tooltipText="Edit Training Cost"
                          onClick={(e) => handleOpenFormDialog(e, trainingCost)}
                          size="20px"
                        />
                      )}
                      {canDelete && (
                        <PATDeleteButton
                          tooltipText="Delete Training Cost"
                          onClick={(e) =>
                            handleOpenDeleteDialog(e, trainingCost.id)
                          }
                          size="20px"
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            />
          ) : (
            <span>No Training Costs Available</span>
          )}
        </AccordionDetails>
      </Accordion>
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setCostToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Training Cost"
        message="Are you sure you want to delete this training cost?"
      />

      <Dialog
        open={openFormDialog}
        onClose={() => handleCloseFormDialog()}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingData ? "Edit Training Cost" : "Add Training Cost"}
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{ mt: 1 }}
          >
            <Box sx={{ mb: 2 }}>
              <DropdownController
                name="title"
                label="Code"
                options={trainingCostOptions}
                control={control}
                errors={errors}
                required
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextFieldController
                name="cost"
                label="Value"
                type="text"
                control={control}
                errors={errors}
                required
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseFormDialog()}>Cancel</Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="primary"
          >
            {editingData ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ParticipantCostTable;
